import React from 'react'
import { Link } from 'react-router-dom'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import './process-page.css'

const ProcessPage = (props) => {
  return (
    <div className="process-page-container1">
      <Helmet>
        <title>ProcessPage - ClearPath Digital</title>
        <meta property="og:title" content="ProcessPage - ClearPath Digital" />
        <link
          rel="canonical"
          href="https://clearpathdigital.teleporthq.app/process-page"
        />
      </Helmet>
      <Navigation></Navigation>
      <section className="process-hero">
        <div className="process-hero-media">
          <video
            src="https://videos.pexels.com/video-files/7989443/7989443-hd_1280_720_25fps.mp4"
            loop="true"
            muted="true"
            autoPlay="true"
            playsInline="true"
            className="process-hero-video"
          ></video>
          <div className="process-hero-overlay"></div>
        </div>
        <div className="process-hero-container">
          <div className="process-hero-content">
            <h1 className="hero-title">
              From Discovery to Launch: A Predictable Path to Growth
            </h1>
            <p className="hero-subtitle">
              We don&apos;t just &quot;design&quot; websites. We follow a
              battle-tested, end-to-end strategic process that removes risk and
              ensures your new site is a high-converting sales asset from day
              one.
            </p>
            <div className="process-hero-actions">
              <Link to="/contact-book-a-call">
                <div className="process-page-thq-btn-elm btn btn-primary btn-lg">
                  <span>Book a free website strategy call</span>
                </div>
              </Link>
            </div>
            <p className="process-hero-reassurance">
              Zero guesswork. Clear milestones. Guaranteed delivery.
            </p>
          </div>
        </div>
      </section>
      <section className="process-timeline">
        <div className="process-timeline-header">
          <h2 className="section-title">Your 6-Week Roadmap to Launch</h2>
          <p className="section-subtitle">
            A clear, week-by-week breakdown of milestones and deliverables.
          </p>
        </div>
        <div className="process-timeline-wrapper">
          <div className="process-timeline-track">
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 1</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">
                  Discovery &amp; Strategy
                </h3>
                <p className="process-timeline-text">
                  Deep dive into your business goals, audience, and conversion
                  flow.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: Strategy Brief
                </span>
              </div>
            </div>
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 2</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">
                  Structure &amp; Copy
                </h3>
                <p className="process-timeline-text">
                  Building the sitemap and high-converting copy that speaks to
                  your clients.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: Wireframes
                </span>
              </div>
            </div>
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 3</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">Visual Design</h3>
                <p className="process-timeline-text">
                  Applying your brand to the high-fidelity UI design for mobile
                  and desktop.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: Design Prototypes
                </span>
              </div>
            </div>
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 4</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">Development</h3>
                <p className="process-timeline-text">
                  Translating designs into clean, fast-loading, and responsive
                  code.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: Staging Link
                </span>
              </div>
            </div>
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 5</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">
                  Testing &amp; Refinement
                </h3>
                <p className="process-timeline-text">
                  Rigorous QA across all devices and final content polishing.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: QA Report
                </span>
              </div>
            </div>
            <div className="process-timeline-item">
              <div className="process-timeline-marker">
                <span>Week 6</span>
              </div>
              <div className="process-timeline-card">
                <h3 className="process-timeline-step-title">
                  Launch &amp; Training
                </h3>
                <p className="process-timeline-text">
                  Going live and training your team on how to manage your new
                  asset.
                </p>
                <span className="process-timeline-deliverable">
                  Deliverable: Live Site
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="process-phases">
        <div className="process-phases-inner">
          <h2 className="section-title">Deep Dive into Our Methodology</h2>
          <div className="process-steps-vertical">
            <div className="process-step">
              <div className="process-step-number">
                <span>01</span>
              </div>
              <div className="process-step-content">
                <h3 className="process-step-title">
                  Phase 1: Discovery &amp; Research
                </h3>
                <p className="section-content">
                  We start by understanding your business model, target
                  audience, and current pain points. We analyze why your current
                  site (if any) is failing to convert and define the primary
                  goals for the new project.
                </p>
                <ul className="process-step-list">
                  <li>
                    <span>Stakeholder interviews &amp; goal setting</span>
                  </li>
                  <li>
                    <span>Competitor conversion analysis</span>
                  </li>
                  <li>
                    <span>User persona mapping</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number">
                <span>02</span>
              </div>
              <div className="process-step-content">
                <h3 className="process-step-title">
                  Phase 2: Strategy &amp; Structure
                </h3>
                <p className="section-content">
                  We map out the user journey. This isn&apos;t about colors yet;
                  it&apos;s about the flow. We create a sitemap and low-fidelity
                  wireframes that focus on hierarchy and message clarity.
                </p>
                <ul className="process-step-list">
                  <li>
                    <span>Conversion-focused sitemap</span>
                  </li>
                  <li>
                    <span>Interactive wireframe prototypes</span>
                  </li>
                  <li>
                    <span>Sales-driven copy guidance</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number">
                <span>03</span>
              </div>
              <div className="process-step-content">
                <h3 className="process-step-title">
                  Phase 3: High-Fidelity Design
                </h3>
                <p className="section-content">
                  We bring the structure to life with a modern, professional
                  visual identity. Every design decision is made to build trust
                  and guide the user toward your primary Call to Action.
                </p>
                <ul className="process-step-list">
                  <li>
                    <span>Custom UI design system</span>
                  </li>
                  <li>
                    <span>Mobile-first responsive layouts</span>
                  </li>
                  <li>
                    <span>Accessibility &amp; contrast optimization</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number">
                <span>04</span>
              </div>
              <div className="process-step-content">
                <h3 className="process-step-title">
                  Phase 4: Clean Development
                </h3>
                <p className="section-content">
                  Our developers build your site using clean, semantic code. We
                  prioritize speed, security, and ease of use, ensuring you have
                  full control over your content through a modern CMS.
                </p>
                <ul className="process-step-list">
                  <li>
                    <span>SEO-friendly semantic HTML5</span>
                  </li>
                  <li>
                    <span>Lightning-fast performance optimization</span>
                  </li>
                  <li>
                    <span>Secure CMS integration</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-number">
                <span>05</span>
              </div>
              <div className="process-step-content">
                <h3 className="process-step-title">
                  Phase 5: Testing &amp; Deployment
                </h3>
                <p className="section-content">
                  Before launch, we run a 50-point checklist. We test across 15+
                  browser and device combinations to ensure a flawless
                  experience for every single visitor.
                </p>
                <ul className="process-step-list">
                  <li>
                    <span>Cross-browser compatibility testing</span>
                  </li>
                  <li>
                    <span>Google PageSpeed optimization</span>
                  </li>
                  <li>
                    <span>Final SEO &amp; analytics configuration</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="process-inputs">
        <div className="process-inputs-inner">
          <div className="process-inputs-header">
            <h2 className="section-title">What We Need From You</h2>
            <p className="section-subtitle">
              To maintain our 6-week timeline, we require clear inputs and
              timely feedback.
            </p>
          </div>
          <div className="process-grid-3x2">
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 2v4m8-4v4"></path>
                    <rect x="3" y="4" rx="2" width="18" height="18"></rect>
                    <path d="M3 10h18"></path>
                  </g>
                </svg>
              </div>
              <h3 className="process-input-title">Onboarding Form</h3>
              <p className="section-content">
                A detailed brief covering your business goals, target audience,
                and brand values.
              </p>
            </div>
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                  </g>
                </svg>
              </div>
              <h3 className="process-input-title">Content &amp; Copy</h3>
              <p className="section-content">
                Final text, high-res images, and logos. We provide templates to
                make this easy.
              </p>
            </div>
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="m16 18l6-6l-6-6M8 6l-6 6l6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
              <h3 className="process-input-title">Access Credentials</h3>
              <p className="section-content">
                Domain registrar and hosting access (if applicable) for a smooth
                deployment.
              </p>
            </div>
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
              <h3 className="process-input-title">Timely Feedback</h3>
              <p className="section-content">
                Consolidated feedback within 48 hours of each milestone
                delivery.
              </p>
            </div>
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m12 15l2 2l4-4"></path>
                    <rect
                      x="8"
                      y="8"
                      rx="2"
                      ry="2"
                      width="14"
                      height="14"
                    ></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </g>
                </svg>
              </div>
              <h3 className="process-input-title">Final Approval</h3>
              <p className="section-content">
                A designated decision-maker to sign off on each phase to avoid
                scope creep.
              </p>
            </div>
            <div className="process-input-card">
              <div className="process-input-icon">
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M16 5H3m13 7H3m8 7H3m12-1l2 2l4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
              <h3 className="process-input-title">Checklist Completion</h3>
              <p className="section-content">
                Following our &quot;avoiding delays&quot; guide to ensure we
                stay on track for launch.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="process-assurances">
        <div className="process-assurances-inner">
          <div className="process-assurances-header">
            <h2 className="section-title">Risk-Free Delivery</h2>
            <p className="section-subtitle">
              We build trust through transparency and rigorous quality control.
            </p>
          </div>
          <div className="process-grid-3x2">
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">Fixed Revision Policy</h3>
              <p className="section-content">
                Two rounds of comprehensive revisions at each phase to ensure
                the result is exactly what you need.
              </p>
            </div>
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">50-Point QA Checklist</h3>
              <p className="section-content">
                Every site undergoes rigorous testing for speed, security,
                links, and mobile responsiveness before launch.
              </p>
            </div>
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">Rollback Safety Plan</h3>
              <p className="section-content">
                We maintain full backups and a 24-hour rollback plan during
                launch to ensure zero downtime.
              </p>
            </div>
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">Timeline Buffers</h3>
              <p className="section-content">
                Our 6-week schedule includes built-in buffers for unforeseen
                technical challenges, ensuring we never miss a deadline.
              </p>
            </div>
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">Approval Gates</h3>
              <p className="section-content">
                No phase begins without your explicit sign-off on the previous
                one, preventing costly late-stage changes.
              </p>
            </div>
            <div className="process-assurance-item">
              <h3 className="process-assurance-title">Post-Launch Support</h3>
              <p className="section-content">
                30 days of technical support after launch to handle any minor
                adjustments or questions as you go live.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="process-deliverables">
        <div className="process-deliverables-inner">
          <div className="process-deliverables-header">
            <h2 className="section-title">What You Get at Launch</h2>
            <p className="section-subtitle">
              A complete digital asset, ready to drive sales and grow your
              business.
            </p>
          </div>
          <div className="process-grid-3x2">
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">Full CMS Access</h3>
              <p className="section-content">
                Complete ownership and administrative access to your
                website&apos;s backend.
              </p>
            </div>
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">Training Library</h3>
              <p className="section-content">
                Custom video tutorials showing you how to update text, images,
                and blog posts.
              </p>
            </div>
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">Performance Report</h3>
              <p className="section-content">
                A Google PageSpeed Insights report confirming your site&apos;s
                lightning-fast load times.
              </p>
            </div>
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">SEO Foundation</h3>
              <p className="section-content">
                Properly configured meta tags, sitemaps, and search engine
                indexing setup.
              </p>
            </div>
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">Security Setup</h3>
              <p className="section-content">
                SSL certificate installation, firewall configuration, and
                automated backup schedule.
              </p>
            </div>
            <div className="process-deliverable-card">
              <h3 className="process-deliverable-title">Analytics Dashboard</h3>
              <p className="section-content">
                Integrated Google Analytics and conversion tracking to measure
                your ROI.
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="process-page-container2">
        <div className="process-page-container3">
          <Script
            html={`<script defer data-name="process-page-logic">
(function(){
  // Handle smooth scroll for timeline
  const timelineWrapper = document.querySelector('.process-timeline-wrapper');
  if (timelineWrapper) {
    let isDown = false;
    let startX;
    let scrollLeft;

    timelineWrapper.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - timelineWrapper.offsetLeft;
      scrollLeft = timelineWrapper.scrollLeft;
    });

    timelineWrapper.addEventListener('mouseleave', () => {
      isDown = false;
    });

    timelineWrapper.addEventListener('mouseup', () => {
      isDown = false;
    });

    timelineWrapper.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - timelineWrapper.offsetLeft;
      const walk = (x - startX) * 2;
      timelineWrapper.scrollLeft = scrollLeft - walk;
    });
  }

  // Simple reveal animation on scroll for grid items
  const revealItems = document.querySelectorAll('.process-input-card, .process-assurance-item, .process-deliverable-card, .process-step');
  
  const revealOnScroll = () => {
    const triggerBottom = window.innerHeight * 0.9;
    
    revealItems.forEach(item => {
      const itemTop = item.getBoundingClientRect().top;
      if (itemTop < triggerBottom) {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }
    });
  };

  // Initialize styles for animation
  revealItems.forEach(item => {
    item.style.opacity = '0';
    item.style.transform = 'translateY(20px)';
    item.style.transition = 'all 0.6s ease-out';
  });

  window.addEventListener('scroll', revealOnScroll);
  revealOnScroll(); // Run once on load
})()
</script>`}
          ></Script>
        </div>
      </div>
      <Footer></Footer>
    </div>
  )
}

export default ProcessPage
